"use client";

import { useLayoutEffect, useRef } from "react";
import { CurrencyIcon } from "@/components/ui/CurrencyIcons";
import { playDooberFly } from "@/lib/audio/play";
import type { PendingEventIconFly } from "@/lib/eventIconFlyToMenu";

function quadBez1d(t: number, p0: number, p1: number, p2: number): number {
  const u = 1 - t;
  return u * u * p0 + 2 * u * t * p1 + t * t * p2;
}

function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10001) / 10000;
}

export type EventIconFlySpec = {
  id: string;
  eventKey: string;
  sx: number;
  sy: number;
  dx: number;
  dy: number;
  delay: number;
  completed: boolean;
  label: string;
  bonusCoins: number;
};

function buildEventIconFlyKeyframes(spec: EventIconFlySpec): Keyframe[] {
  const { dx, dy, id } = spec;
  const r1 = hash01(`${id}:arc`);
  const r2 = hash01(`${id}:sway`);
  const arcLift = 72 + r1 * 60;
  const sway = (r2 - 0.5) * 64;
  const p1x = dx * 0.48 + sway;
  const p1y = dy * 0.28 - arcLift;
  const steps = [0, 0.12, 0.28, 0.44, 0.58, 0.72, 0.82, 0.9, 1];
  return steps.map((t) => {
    const x = quadBez1d(t, 0, p1x, dx);
    const y = quadBez1d(t, 0, p1y, dy);
    let sc: number;
    if (t < 0.75) {
      const u = t / 0.75;
      const swell = Math.sin(u * Math.PI);
      sc = 0.95 + swell * 0.18;
    } else if (t < 0.88) {
      const u = (t - 0.75) / 0.13;
      sc = 1.13 - u * 0.5;
    } else {
      const u = (t - 0.88) / 0.12;
      sc = 0.63 + u * 0.12;
    }
    return {
      transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) scale(${sc.toFixed(3)})`,
      offset: t,
    };
  });
}

function EventFlyChip({
  completed,
  label,
  bonusCoins,
}: {
  completed: boolean;
  label: string;
  bonusCoins: number;
}) {
  if (completed && bonusCoins > 0) {
    return (
      <div
        className="flex items-center gap-0.5 rounded-full border-[2px] border-amber-800/70 bg-gradient-to-b from-amber-100 via-amber-300 to-amber-500 px-2 py-0.5 font-display text-[0.62rem] font-black tabular-nums uppercase tracking-wide text-amber-950 shadow-[0_4px_14px_rgba(0,0,0,0.4)]"
        aria-hidden
      >
        <CurrencyIcon kind="coin" size={14} className="h-3 w-3 shrink-0" />
        <span>+{bonusCoins}</span>
      </div>
    );
  }
  return (
    <div
      className="rounded-full border-[2px] border-emerald-900/65 bg-gradient-to-b from-emerald-200 via-emerald-400 to-emerald-700 px-2 py-0.5 font-display text-[0.62rem] font-black tabular-nums text-emerald-950 shadow-[0_4px_14px_rgba(0,0,0,0.38)]"
      aria-hidden
    >
      {label}
    </div>
  );
}

function queryEventFloatEl(eventKey: string): Element | null {
  try {
    return document.querySelector(`[data-cwf-event-float="${CSS.escape(eventKey)}"]`);
  } catch {
    return document.querySelector(`[data-cwf-event-float="${eventKey}"]`);
  }
}

export function buildSpecsFromPendingFlies(
  pending: PendingEventIconFly[],
): EventIconFlySpec[] {
  if (typeof window === "undefined" || pending.length === 0) return [];
  const sx = window.innerWidth * 0.52;
  const sy = window.innerHeight * 0.46;
  return pending.map((p, i) => {
    const el = queryEventFloatEl(p.eventKey);
    const rect = el?.getBoundingClientRect();
    const tcx = rect
      ? rect.left + rect.width / 2
      : 48 + window.innerWidth * 0.04;
    const tcy = rect
      ? rect.top + rect.height / 2
      : window.innerHeight * 0.48;
    return {
      id: `evfly-${p.eventKey}-${i}`,
      eventKey: p.eventKey,
      sx,
      sy,
      dx: tcx - sx,
      dy: tcy - sy,
      delay: i * 72,
      completed: p.completed,
      label: `${p.newProgress}/${p.target}`,
      bonusCoins: p.bonusCoins,
    };
  });
}

export function EventIconFlyLayer(props: {
  specs: EventIconFlySpec[];
  onAllFinished: () => void;
}) {
  const { specs, onAllFinished } = props;
  const refMap = useRef(new Map<string, HTMLDivElement | null>());
  const onDoneRef = useRef(onAllFinished);
  onDoneRef.current = onAllFinished;

  useLayoutEffect(() => {
    if (specs.length === 0) return;
    try {
      playDooberFly();
    } catch {
      /* noop */
    }
    const anims: Animation[] = [];
    let finished = 0;
    const bump = () => {
      finished += 1;
      if (finished >= specs.length) {
        onDoneRef.current();
      }
    };
    for (const spec of specs) {
      const el = refMap.current.get(spec.id);
      if (!el) {
        bump();
        continue;
      }
      const anim = el.animate(buildEventIconFlyKeyframes(spec), {
        duration: 780,
        delay: spec.delay,
        easing: "cubic-bezier(0.22, 0.04, 0.2, 1)",
        fill: "forwards",
      });
      anims.push(anim);
      anim.onfinish = () => bump();
    }
    return () => {
      for (const a of anims) {
        try {
          a.cancel();
        } catch {
          /* noop */
        }
      }
    };
  }, [specs]);

  return (
    <>
      {specs.map((spec) => (
        <div
          key={spec.id}
          className="pointer-events-none fixed z-[125]"
          style={{
            left: `${spec.sx}px`,
            top: `${spec.sy}px`,
            transform: "translate(-50%, -50%)",
          }}
          aria-hidden
        >
          <div
            ref={(el) => {
              refMap.current.set(spec.id, el);
            }}
            className="relative will-change-transform"
          >
            <EventFlyChip
              completed={spec.completed}
              label={spec.label}
              bonusCoins={spec.bonusCoins}
            />
          </div>
        </div>
      ))}
    </>
  );
}
